import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

import type { OrgResponse } from "@bw/shared/types/generated";
import { formatDate } from "@bw/shared/utils";

import { StatusBadge } from "@/components/StatusBadge";
import { orgBusinessName, orgLegalName } from "@/shared/orgDisplay";

import { daysSince } from "./terms";

export function ApprovalRow({
  org,
  active,
  disabled,
  onSelect,
  children,
}: {
  org: OrgResponse;
  active: boolean;
  disabled: boolean;
  onSelect: () => void;
  children?: ReactNode;
}) {
  const blockers = org.approval_blockers?.length ?? 0;

  return (
    <Box component="article" sx={{ borderBottom: 1, borderColor: "divider" }}>
      <ButtonBase
        type="button"
        disabled={disabled}
        onClick={onSelect}
        aria-expanded={active}
        aria-controls={active ? `approval-details-${org.org_id}` : undefined}
        sx={{
          "&&": { pointerEvents: "auto" },
          display: "block",
          width: "100%",
          px: 2,
          py: 1.75,
          textAlign: "left",
          bgcolor: active ? "action.selected" : undefined,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="p"
              variant="subtitle1"
              sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
            >
              {orgBusinessName(org)}
            </Typography>
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ mt: 0.25 }}
            >
              {orgLegalName(org) ?? "Legal name not provided"}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ flexWrap: "wrap", mt: 1 }}
            >
              <Typography color="text.secondary" variant="caption">
                {daysSince(org.created_at)}d in queue
              </Typography>
              <Typography color="text.secondary" variant="caption">
                Submitted {formatDate(org.created_at)}
              </Typography>
              {blockers > 0 && (
                <Typography color="warning.main" variant="caption">
                  {blockers} blocker{blockers === 1 ? "" : "s"}
                </Typography>
              )}
            </Stack>
          </Box>
          <StatusBadge status={org.status} />
        </Stack>
      </ButtonBase>

      {active && <Box sx={{ px: 2, pb: 2 }}>{children}</Box>}
    </Box>
  );
}
